import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Eye, Search, ChevronLeft, ChevronRight, 
  Link, Copy, ListChecks, X, CheckSquare 
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import DetalhesClienteModal from '../../components/DetalhesClienteModal';

const ITEMS_PER_PAGE = 20;

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modais
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false); // NOVO: Modal de verificação
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [agenciaSlug, setAgenciaSlug] = useState(null);

  // State de Seleção
  const [selectedClients, setSelectedClients] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [formData, setFormData] = useState({
    nome: '', email: '', telefone: '', cpf: '', data_nascimento: '',
  });

  const loadClientes = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/clientes');
      setClientes(response.data || []);
      setCurrentPage(1);
      setSelectedClients([]); 
    } catch (error) {
      toast.error('Erro ao carregar clientes');
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAgenciaSlug = async () => {
      try {
        const response = await axiosInstance.get('/agencia/config');
        if (response.data && response.data.slug) {
          setAgenciaSlug(response.data.slug);
        }
      } catch (error) {
        console.error("Erro ao carregar configurações da agência", error);
      }
    };
    fetchAgenciaSlug();
  }, []);

  useEffect(() => { loadClientes(); }, []);

  // --- FUNÇÕES DE FORMATAÇÃO ---
  const formatCPF = (cpf) => { if (!cpf) return ''; const cleanedCpf = String(cpf).replace(/\D/g, '').padStart(11, '0'); return cleanedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"); };
  
  const formatDate = (dateString) => {
    if (!dateString || String(dateString).trim() === '') { return 'Não informada'; }
    try {
      let date;
      if (String(dateString).includes('T')) { date = new Date(dateString); }
      else if (String(dateString).match(/^\d{4}-\d{2}-\d{2}$/)) { date = new Date(dateString + 'T00:00:00Z'); }
      else {
        const timestamp = Date.parse(dateString);
        if (!isNaN(timestamp)) date = new Date(timestamp);
      }
      if (!date || isNaN(date.getTime())) { return 'Data inválida'; }
      return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    } catch (e) { return 'Erro na data'; }
  };

  // --- LÓGICA DE CÓPIA (INDIVIDUAL E MÚLTIPLA) ---
  const generateClientText = (cliente) => {
    return [
        `Nome: ${cliente.nome}`,
        `CPF: ${formatCPF(cliente.cpf)}`,
        `Nascimento: ${formatDate(cliente.data_nascimento)}`,
        cliente.email ? `Email: ${cliente.email}` : '',
        cliente.telefone ? `Tel: ${cliente.telefone}` : ''
    ].filter(Boolean).join('\n');
  };

  const handleCopyData = (cliente) => {
    navigator.clipboard.writeText(generateClientText(cliente));
    toast.success("Dados copiados!");
  };

  // *** NOVO: Copiar Múltiplos ***
  const handleBatchCopy = () => {
    // Busca os objetos completos baseados nos IDs selecionados
    const selectedData = clientes.filter(c => selectedClients.includes(c.id));
    
    if (selectedData.length === 0) return;

    // Gera o texto com separadores
    const fullText = selectedData.map(c => generateClientText(c)).join('\n\n-----------------------------------\n\n');
    
    navigator.clipboard.writeText(fullText);
    toast.success(`${selectedData.length} clientes copiados para a área de transferência!`);
  };

  // --- LÓGICA DE SELEÇÃO ---
  const toggleSelectOne = (id) => {
    setSelectedClients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAllPage = (currentIds) => {
    const allSelected = currentIds.every(id => selectedClients.includes(id));
    if (allSelected) {
        setSelectedClients(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
        const newIds = currentIds.filter(id => !selectedClients.includes(id));
        setSelectedClients(prev => [...prev, ...newIds]);
    }
  };

  const clearSelection = () => setSelectedClients([]);

  // --- MODAIS E CRUD ---
  const handleCopyPublicLink = () => {
    if (!user.agencia_id) { 
        toast.error("Agência não identificada."); 
        return; 
    }

    // ORDEM DE PRIORIDADE:
    // 1. Slug fresco que acabamos de buscar no banco (agenciaSlug)
    // 2. Slug que veio no login (user.agencia_slug)
    // 3. ID da agência (user.agencia_id) - último caso
    const identificador = agenciaSlug || user.agencia_slug || user.agencia_id;

    const link = `${window.location.origin}/cadastro-cliente/${identificador}`;
    
    navigator.clipboard.writeText(link);
    toast.success(`Link copiado! (${identificador})`);
  };

  const handleOpenModal = (cliente = null) => {
    if (cliente) {
      setIsEditing(true);
      setCurrentCliente(cliente);
      let formattedDate = '';
      if (cliente.data_nascimento && String(cliente.data_nascimento).length >= 10) {
          formattedDate = String(cliente.data_nascimento).substring(0, 10);
      }
      setFormData({
        nome: cliente.nome || '',
        email: cliente.email || '',
        telefone: cliente.telefone || '',
        cpf: cliente.cpf || '',
        data_nascimento: formattedDate,
      });
    } else {
      setIsEditing(false);
      setCurrentCliente(null);
      setFormData({ nome: '', email: '', telefone: '', cpf: '', data_nascimento: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData, email: formData.email || null, telefone: formData.telefone || null, data_nascimento: formData.data_nascimento || null };
      if (isEditing) { await axiosInstance.put(`/clientes/${currentCliente.id}`, dataToSend); toast.success('Atualizado!'); }
      else { await axiosInstance.post('/clientes', dataToSend); toast.success('Cadastrado!'); }
      setIsModalOpen(false); loadClientes();
    } catch (error) { toast.error('Erro ao salvar.'); }
  };

  const handleDelete = async (clienteId) => { try { await axiosInstance.delete(`/clientes/${clienteId}`); toast.success('Deletado!'); loadClientes(); } catch (error) { toast.error('Erro ao deletar'); } };
  
  const handleOpenDetailsModal = (cliente) => {
    setCurrentCliente(cliente);
    setIsDetailsModalOpen(true);
  };
  
  const handleCpfChange = (e) => { const onlyNumbers = e.target.value.replace(/\D/g, ''); setFormData({ ...formData, cpf: onlyNumbers.slice(0, 11) }); };

  // --- FILTROS E PAGINAÇÃO ---
  const filteredAndSortedClientes = useMemo(() => {
    if (!Array.isArray(clientes)) return [];
    let items = [...clientes];
    items.sort((a, b) => (a?.nome || '').localeCompare(b?.nome || ''));
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    if (lowerSearchTerm) {
        items = items.filter(cliente => 
            (cliente?.nome && String(cliente.nome).toLowerCase().includes(lowerSearchTerm)) ||
            (cliente?.cpf && String(cliente.cpf).includes(lowerSearchTerm))
        );
    }
    return items;
  }, [clientes, searchTerm]);

  const totalPages = Math.ceil(filteredAndSortedClientes.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentClientes = filteredAndSortedClientes.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const currentIds = currentClientes.map(c => c.id);
  const isAllPageSelected = currentIds.length > 0 && currentIds.every(id => selectedClients.includes(id));

  // Dados dos clientes selecionados (para o Modal de Verificação)
  const selectedClientsData = useMemo(() => {
    return clientes.filter(c => selectedClients.includes(c.id));
  }, [clientes, selectedClients]);

  return (
    <div className="w-full max-w-full p-6">
      
      {/* Header com Barra de Ferramentas de Seleção */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Clientes</h1>
          <p className="text-gray-500 text-sm">Gerencie sua base de clientes</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
            {/* BARRA DE SELEÇÃO ATIVA */}
            {selectedClients.length > 0 && (
                <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 mr-2 animate-in fade-in slide-in-from-right-4">
                    <span className="text-sm font-bold text-blue-700 mr-3 flex items-center">
                        <CheckSquare className="w-4 h-4 mr-1.5" />
                        {selectedClients.length}
                    </span>
                    
                    <div className="h-4 w-px bg-blue-200 mx-2"></div>

                    <Button variant="ghost" size="sm" onClick={() => setIsSelectionModalOpen(true)} className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 h-7 px-2 text-xs font-medium">
                        <ListChecks className="w-3.5 h-3.5 mr-1.5" />
                        Ver Lista
                    </Button>
                    
                    <Button variant="ghost" size="sm" onClick={handleBatchCopy} className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 h-7 px-2 text-xs font-medium ml-1">
                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                        Copiar Todos
                    </Button>

                    <Button variant="ghost" size="icon" onClick={clearSelection} className="h-6 w-6 ml-2 text-blue-400 hover:text-red-500 hover:bg-red-50 rounded-full" title="Limpar seleção">
                        <X className="w-3.5 h-3.5" />
                    </Button>
                </div>
            )}

            <Button variant="outline" onClick={handleCopyPublicLink} className="border-blue-200 text-blue-700 hover:bg-blue-50">
                <Link className="w-4 h-4 mr-2" /> Link Auto-Cadastro
            </Button>

            <Button onClick={() => handleOpenModal()} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Plus className="w-4 h-4 mr-2" /> Novo Cliente
            </Button>
        </div>
      </div>

      <div className="mb-6 relative max-w-md">
         <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
         <Input placeholder="Buscar por nome ou CPF..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {/* --- MODAL DE GERENCIAMENTO DE SELEÇÃO --- */}
      <Dialog open={isSelectionModalOpen} onOpenChange={setIsSelectionModalOpen}>
        <DialogContent className="max-w-md">
            <DialogHeader>
                <DialogTitle>Clientes Selecionados ({selectedClients.length})</DialogTitle>
                <DialogDescription>Revise a lista antes de copiar ou realizar ações em massa.</DialogDescription>
            </DialogHeader>
            
            <div className="mt-4 max-h-[300px] overflow-y-auto border rounded-md divide-y">
                {selectedClientsData.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">Nenhum cliente selecionado.</div>
                ) : (
                    selectedClientsData.map(client => (
                        <div key={client.id} className="flex justify-between items-center p-3 hover:bg-gray-50">
                            <div>
                                <p className="font-medium text-sm text-gray-900">{client.nome}</p>
                                <p className="text-xs text-gray-500">{formatCPF(client.cpf)}</p>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => toggleSelectOne(client.id)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    ))
                )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                <Button variant="outline" onClick={clearSelection} className="text-red-600 border-red-100 hover:bg-red-50">
                    Limpar Tudo
                </Button>
                <Button onClick={handleBatchCopy} className="bg-blue-600 hover:bg-blue-700">
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Lista
                </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- TABELA (Conteúdo Principal) --- */}
      {loading ? ( <p className="text-center text-gray-500 py-4">Carregando...</p> ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="w-12 px-6 py-4 text-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer" checked={isAllPageSelected} onChange={() => toggleSelectAllPage(currentIds)} />
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CPF</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Nascimento</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {currentClientes.map((cliente) => {
                    const isSelected = selectedClients.includes(cliente.id);
                    return (
                        <tr key={cliente.id} className={`transition-colors ${isSelected ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}>
                            <td className="px-6 py-4 text-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer" checked={isSelected} onChange={() => toggleSelectOne(cliente.id)} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cliente.nome}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{formatCPF(cliente.cpf)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(cliente.data_nascimento)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                <div className="flex items-center justify-end gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={() => handleCopyData(cliente)} title="Copiar individual">
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={() => handleOpenDetailsModal(cliente)}>
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-green-600" onClick={() => handleOpenModal(cliente)}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader><AlertDialogTitle>Excluir cliente?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(cliente.id)} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </td>
                        </tr>
                    );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                      <Button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} variant="outline" size="sm">Anterior</Button>
                      <Button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} variant="outline" size="sm">Próxima</Button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <p className="text-sm text-gray-700">Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span></p>
                      <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                      </div>
                  </div>
              </div>
          )}
        </div>
      )}

      {/* Modal de Formulário */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isEditing ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div><Label htmlFor="nome">Nome Completo</Label><Input id="nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
                <div><Label htmlFor="telefone">Telefone</Label><Input id="telefone" value={formData.telefone} onChange={(e) => setFormData({ ...formData, telefone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="cpf">CPF</Label><Input id="cpf" value={formData.cpf} onChange={handleCpfChange} maxLength={11} required /></div>
                <div><Label htmlFor="data_nascimento">Nascimento</Label><Input id="data_nascimento" type="date" value={formData.data_nascimento} onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="submit" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Salvar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
       <DetalhesClienteModal cliente={currentCliente} isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} />
    </div>
  );
}